import { buildLegacyChubbPayload, hasAuthenticDemographics, isAuthenticGender } from '@/services/chubbLegacyQuote';
import type { useQuoteGeneration } from '@/composables/useQuoteGeneration';
import type { useQuoteStore } from '@/stores/quoteStore';
import type { PersonalData } from '@/types/quote.types';
import type { DriverFormState } from './useDriverForm';

type QuoteGeneration = ReturnType<typeof useQuoteGeneration>;
type QuoteStore = ReturnType<typeof useQuoteStore>;

/** Cotizacion returned by `generateQuote` (only the fields the widget reads). */
export interface GeneratedCotizacion {
  Id_Cotizacion?: number | string;
  Id_Cotizacion_Autos_Detalle?: number | string;
}

const text = (value?: string): string => value || '';

/** Persists the generated cotizacion ids in the store. */
export const storeCotizacionIds = (quoteStore: QuoteStore, cotizacion: GeneratedCotizacion): void => {
  if (!quoteStore.quoteData.cotizacionData) {
    quoteStore.quoteData.cotizacionData = {};
  }
  quoteStore.quoteData.cotizacionData.idCotizacion = Number(cotizacion.Id_Cotizacion);
  if (cotizacion.Id_Cotizacion_Autos_Detalle) {
    quoteStore.quoteData.cotizacionData.idCotizacionAutosDetalle = Number(
      cotizacion.Id_Cotizacion_Autos_Detalle,
    );
  }
};

/** Fetches Qualitas/ANA quotes and then the Chubb quote for a cotizacion. */
export const fetchInsurersAndChubb = async (
  quoteStore: QuoteStore,
  quoteGeneration: QuoteGeneration,
  idCotizacion: number,
): Promise<void> => {
  await quoteGeneration.fetchQuotes({ idCotizacion, formaPago: 'C', paquete: '1', showGlobalLoading: false, company: 'BOTH' });
  await quoteGeneration.fetchChubbQuote(
    buildLegacyChubbPayload(quoteStore.quoteData),
    'C',
    '1',
  );
};

/** At least one insurer (Qualitas or ANA) returned coverage. */
export const hasAvailableQuotes = (quoteGeneration: QuoteGeneration): boolean => {
  const present = (value: unknown): boolean => value !== null && value !== undefined;
  return (
    present(quoteGeneration.quotesResponse.value?.Cobertura) ||
    present(quoteGeneration.anaQuotes.value?.Cobertura)
  );
};

/** Gender only when it is explicit and the demographics are authentic. */
export const resolveAuthenticGender = (form: DriverFormState): 'M' | 'F' | null =>
  isAuthenticGender(form.gender) && hasAuthenticDemographics(form.gender, form.birthDate)
    ? form.gender
    : null;

/** Technical demographics for the quick quote (no Chubb, no xcf). */
export const buildQuickPersonalData = (postalCode: string): PersonalData => ({
  fullName: '',
  gender: 'M',
  postalCode: postalCode.padStart(5, '0'), // Asegurar 5 dígitos (ej. 1900 -> 01900)
  birthDate: '1990-01-01',
  email: '',
  whatsapp: '',
  privacyConsent: true,
});

/** Authentic demographics collected by the Chubb gate. */
export const buildGatePersonalData = (form: DriverFormState, gender: 'M' | 'F'): PersonalData => ({
  fullName: text(form.fullName),
  gender,
  postalCode: form.postalCode.padStart(5, '0'),
  birthDate: form.birthDate,
  email: text(form.email),
  whatsapp: text(form.whatsapp),
  idEstado: form.idEstado,
  idCiudad: form.idCiudad,
  idMunicipio: form.idMunicipio,
  privacyConsent: true,
});

/** Driver data with CRM fallbacks for Qualitas/ANA when Chubb demographics are missing. */
export const buildCrmPersonalData = (
  form: DriverFormState,
  authenticGender: 'M' | 'F' | null,
  chubbEnabled: boolean,
): PersonalData => {
  const explicitGender = form.gender === 'M' || form.gender === 'F' ? form.gender : 'M';
  return {
    gender: authenticGender ?? explicitGender,
    postalCode: form.postalCode.padStart(5, '0'),
    birthDate: form.birthDate || (chubbEnabled ? '' : '1990-01-01'),
    email: form.email,
    fullName: form.fullName,
    whatsapp: form.whatsapp,
    idEstado: form.idEstado,
    idCiudad: form.idCiudad,
    idMunicipio: form.idMunicipio,
    privacyConsent: form.privacyConsent,
  };
};
